/* eslint-disable @typescript-eslint/camelcase */
/**
 * This module specify all the handlers used for tracking.
 * All handlers will receive
 * - a videoTracker object as its 1st argument
 * - all relevant arguments (i.e. listener's events) will be passed down from the 2nd... arguments
 */

import moment from 'moment';
import BrowserDetector from '@/scripts/helpers/browser-detector';
import throttle from 'lodash/throttle';

import webcall from '@/scripts/webcall';
import predicate from '@/scripts/helpers/predicate';

import analytic from '@/scripts/libs/analytic';
import VideoTracker from '.';
import { TrackerTrackEventName, VideoTrackerPlayerTimeProduct } from '@/scripts/models/VideoPagePlayerOptions';
import { assertPresenceOfPlayer } from './helpers';

const LOGGING_THROTTLE_TIMEMS = 250;

const eventHandlers: EventHandlers = {
  /**
   * All players related events.
   * Handler specified here will be automatically binded to the player's event emitter
   */
  playerEventHandlers: {
    // Player events
    ready(videoTracker): void {
      const player = assertPresenceOfPlayer(videoTracker.player);

      analytic.send({
        category: 'video',
        action: 'impression',
        label: videoTracker.video.id,
        property: videoTracker.video.title,
        customProperties: {
          snowplow: {
            property: JSON.stringify({
              session_id: videoTracker.seasonUuid,
              player: {
                mute: player.getMute(),
              },
            }),
          },
        },
      });
    },
    firstFrame(videoTracker, event: jwplayer.FirstFrameParam): void {
      videoTracker.sendToTracker('play', {
        ...(
          videoTracker.playerOptions.player_type === VideoTrackerPlayerTimeProduct.Embed
            ? {
              parent_url: videoTracker.playerOptions.parent_url,
            }
            : {}
        ),
        failover: videoTracker.state.goneIntoFallback,
        loadTime: event.loadTime,
        ...(
          videoTracker.state.goneIntoFallback
            ? {
              error_message: videoTracker.state.errorMessage,
            }
            : {}
        ),
      });

      webcall.video.postVideoWatch({
        video_id: videoTracker.video.id,
      }, () => {
        // _log('videowatch success');
      });

      const player = assertPresenceOfPlayer(videoTracker.player);

      analytic.send({
        category: 'video',
        action: 'play',
        label: videoTracker.video.id,
        property: videoTracker.video.title,
        customProperties: {
          snowplow: {
            property: JSON.stringify({
              session_id: videoTracker.seasonUuid,
              player: {
                mute: player.getMute(),
              },
            }),
          },
        },
      });

      if (videoTracker.state.goneIntoFallback) {
        analytic.send({
          category: 'video',
          label: videoTracker.video?.id,
          customProperties: {
            snowplow: {
              action: 'failover',
            },
          },
        });
      }

      /*
      window.Intercom('trackEvent', 'player-play', {
        video_uid: videoTracker.video.id,
      });
      */

      // Player Time
      videoTracker.playerTimeTracker.attributes.params.vimeo_loaded_time = event.loadTime;
    },
    play(videoTracker, event): void {
      // Player Time
      // TODO: Nobody is going to set bufferStart to other value
      if (event.oldstate === 'buffering' && videoTracker.state.bufferStart !== null) {
        videoTracker.state.bufferDuration += moment().diff(videoTracker.state.bufferStart, 'milliseconds');
        videoTracker.state.bufferStart = null;
      }
    },
    pause(videoTracker): void {
      analytic.send({
        category: 'video',
        action: 'pause',
        label: videoTracker.video.id,
        property: videoTracker.video.title,
        customProperties: {
          snowplow: {
            property: JSON.stringify({
              session_id: videoTracker.seasonUuid,
            }),
          },
        },
      });

      // Player Time
      videoTracker.playerTimeTracker.attributes.params.isPlayerPause = true;
    },
    seek(videoTracker, event): void {
      if (!videoTracker.state.seeking) {
        videoTracker.state.seeking = true;
        videoTracker.updatePlaytime();
      }

      // Reset isPinged
      videoTracker.state.isPinged = [];
      analytic.send({
        category: 'video',
        action: 'seek',
        label: videoTracker.video.id,
        value: event.position | 0,
        customProperties: {
          ga: false,
          snowplow: {
            property: JSON.stringify({
              session_id: videoTracker.seasonUuid,
            }),
          },
        },
      });
    },
    seeked(videoTracker): void {
      videoTracker.state.seeking = false;
      videoTracker.reportSession('seeked');
    },
    time: throttle((videoTracker, event) => {
      const position = Math.floor(event.position);
      if (!videoTracker.state.isPinged[position]) {
        analytic.send({
          category: 'video',
          action: 'ping',
          label: videoTracker.video.id,
          value: position,
          customProperties: {
            ga: false,
            snowplow: {
              property: JSON.stringify({
                session_id: videoTracker.seasonUuid,
              }),
            },
          },
        });
        videoTracker.state.isPinged[position] = true;
      }
    }, LOGGING_THROTTLE_TIMEMS),
    complete(videoTracker): void {
      videoTracker.state.stopped = true;
      videoTracker.updatePlaytime();

      // TODO: sendToTracker refactor
      // sendToTracker('player_stop', {
      //   user_action: 'exit',
      //  total_time_spent: global.totalPlaytime
      // });

      videoTracker.reportSession('complete');
    },

    // Video option changes
    captionsChanged(videoTracker, event): void {
      const index = event.track;
      const selectedCaption = event.tracks[index];

      analytic.send({
        category: 'video',
        customProperties: {
          ga: {
            action: `subtitle-${selectedCaption.label}`,
            label: videoTracker.video.title,
          },
          snowplow: {
            action: 'subtitle',
            label: videoTracker.video.id,
            property: selectedCaption.label,
          },
        },
      });
    },

    levelsChanged(videoTracker): void {
      // TODO: bufferCount is never changed
      videoTracker.state.bufferCount = 0;
    },

    // Errors
    setupError(videoTracker, event): void {
      videoTracker.sendToTracker('setup_error', {
        message: event.message,
      });
    },
    error(videoTracker, event): void {
      // NOTE: 2020Q1_REVAMP_NOTE videoTracker.video don't have source
      // NOTE: 2020Q1_REVAMP_NOTE (videoTracker.video.source !== 'hosted') will always be evaluated to `true`
      if (!videoTracker.state.goneIntoFallback && (videoTracker.video as any).source !== 'hosted') {
        videoTracker.state.goneIntoFallback = true; // TODO: goneIntoFallback is need to be synced with vs-player. This is not desirable
        videoTracker.sendToTracker('error', {
          message: event.message,
        });
        videoTracker.state.errorMessage = event.message;
      }

      // Player Time
      // NOTE: 2020Q1_REVAMP_NOTE isPlayerError doesn't exist in playerTimeTracker attributes
      (videoTracker.playerTimeTracker.attributes as any).isPlayerError = true;
    },
  },

  /**
   * Custon system Events
   */
  systemEvents: {
    [TrackerTrackEventName.GoIntoHostedFallback](videoTracker: VideoTracker, { originalSource }: { originalSource: string }): void {
      videoTracker.playerTimeTracker.attributes.params.isHostedFailover = true;
      videoTracker.playerTimeTracker.attributes.params.original_source = originalSource;
    },
  },

  /**
   * Custon UI Events
   */
  uiEvents: {
    [TrackerTrackEventName.FirstPlay](videoTracker: VideoTracker): void {
      // Check for mobile device
      const browserIsIphone = (BrowserDetector.os() === 'iOS' && BrowserDetector.md().phone() === 'iPhone');
      const browserIsAndroid = (BrowserDetector.os() === 'AndroidOS');
      const browserIsTwitterWebview = BrowserDetector.isTwitter();
      const browserIsMobile = (browserIsAndroid || browserIsIphone);

      // None mobile or none Twitter in android browser
      // TODO: Question what's so special about Twitter?
      if (
        predicate.if(browserIsAndroid).then(browserIsTwitterWebview) &&
        !browserIsIphone
      ) {
        if (typeof window.fbq === 'function') {
          window.fbq('trackCustom', 'Play');
          window.fbq('track', 'ViewContent', {
            content_ids: [videoTracker.video.id],
            content_type: 'video',
          });
        }
      }

      // Setup tracker params
      const params = {
        country_code: videoTracker.playerOptions.country_code,
        platform: browserIsMobile ? 'mobile' : 'desktop',
        source: videoTracker.playerOptions.service_type,
        video_id: videoTracker.video.id,
      };
      videoTracker.sendToTracker('clickplay', params);

      analytic.send({
        category: 'video',
        action: 'click-play',
        label: videoTracker.video.title,
        customProperties: {
          snowplow: false,
        },
      });
    },
    [TrackerTrackEventName.ContinuePlay](videoTracker: VideoTracker): void {
      analytic.send({
        category: 'video',
        action: 'continue-play',
        label: videoTracker.video.title,
        customProperties: {
          snowplow: false,
        },
      });
    },
    [TrackerTrackEventName.ClickPlay](videoTracker: VideoTracker): void {
      analytic.send({
        category: 'video',
        action: 'click-play',
        label: videoTracker.video.title,
        customProperties: {
          snowplow: false,
        },
      });
    },
    [TrackerTrackEventName.PauseResume](videoTracker: VideoTracker): void {
      analytic.send({
        category: 'video',
        action: 'pause-resume',
        label: videoTracker.video.title,
        customProperties: {
          snowplow: false,
        },
      });
    },
  },

  /**
   * Global windows event
   */
  windowEvents: {
    beforeunload(videoTracker: VideoTracker): void {
      const player = assertPresenceOfPlayer(videoTracker.player);
      const currentPlayerState = player.getState();

      /**
       * NOTE: 2020Q1_REVAMP_NOTE
       * Original logic was
       * currentPlayerState !== 'paused' || currentPlayerState !== 'completed'
       * But this currentPlayerState !== 'completed' will never be checked
       * I think the intension was. currentPlayerState is neither paused nor completed
       *
       * Therefore, I think the correct logic should be?
       * currentPlayerState !== 'paused' && currentPlayerState !== 'completed'?
       */
      if (currentPlayerState !== 'paused' && currentPlayerState !== 'completed') {
        videoTracker.updatePlaytime();
        videoTracker.sendToTracker('stop', {
          ...(
            videoTracker.playerOptions.player_type === VideoTrackerPlayerTimeProduct.Embed
              ? { parent_url: videoTracker.playerOptions.parent_url }
              : {}
          ),
          user_action: 'exit',
          total_time_spent: videoTracker.state.totalPlaytime,
        });
        videoTracker.reportSession('unload');

        // Player Time
        if (currentPlayerState === 'idle') {
          return;
        }

        // NOTE: 2020Q1_REVAMP_NOTE attributes object is shallow cloned before passing to sendPlayerTimeToTracker
        // NOTE: 2020Q1_REVAMP_NOTE this is done as the modified attributes object does not comform with the existing defined model
        const attributes = {
          ...videoTracker.playerTimeTracker.attributes,
          play_time: videoTracker.state.playtime,
          params: {
            ...videoTracker.playerTimeTracker.attributes.params,
            re_buffering_count: videoTracker.state.bufferCount,
            buffering_duration: videoTracker.state.bufferDuration,
            source_time: videoTracker.state.sourceTime,
            total_time: 0,
            total_time_play: player.getPosition() * 1000,
            product: videoTracker.playerOptions.player_type,
            source: videoTracker.playerOptions.service_type,
            parent_url: undefined as (string | undefined | null),
          },
        };

        // Embed ONLY
        if (videoTracker.playerOptions.player_type === VideoTrackerPlayerTimeProduct.Embed) {
          attributes.params.parent_url = videoTracker.playerOptions.parent_url;
        }

        videoTracker.sendPlayerTimeToTracker('time', attributes.params);
      }
    },
  },
};

export default {
  // Export each category
  ...eventHandlers,

  // And everything flatten
  all: Object.entries(eventHandlers)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .reduce((acc, [_category, handlers]) => {
      acc = {
        ...acc,
        ...handlers,
      };
      return acc as CombinedEventHandlers;
    }, {} as CombinedEventHandlers)
  ,
};

export type PlayerEventParam = jwplayer.EventParams[keyof jwplayer.EventParams];
export type PlayerEventHandler<EventT = PlayerEventParam> = (videoTracker: VideoTracker, event: EventT) => void;

export interface EventHandlers {
  playerEventHandlers: {
    ready: PlayerEventHandler<jwplayer.ReadyParam>;
    firstFrame: PlayerEventHandler<jwplayer.FirstFrameParam>;
    play: PlayerEventHandler<jwplayer.PlayParam>;
    pause: PlayerEventHandler<jwplayer.PlayParam>;
    seek: PlayerEventHandler<jwplayer.SeekParam>;
    seeked: PlayerEventHandler<never>;
    time: PlayerEventHandler<jwplayer.TimeParam>;
    complete: PlayerEventHandler<never>;
    captionsChanged: PlayerEventHandler<{
      tracks: Array<{
        id?: string;
        label?: string;
        language?: string;
      }>;
      track: number;
      type: 'captionsChanged';
    }>;
    levelsChanged: PlayerEventHandler<jwplayer.LevelsChangedParam>;
    setupError: PlayerEventHandler<jwplayer.ErrorParam>;
    error: PlayerEventHandler<jwplayer.ErrorParam>;
  };
  systemEvents: {
    [TrackerTrackEventName.GoIntoHostedFallback]: PlayerEventHandler<never>;
  };
  uiEvents: {
    [TrackerTrackEventName.FirstPlay]: PlayerEventHandler<never>;
    [TrackerTrackEventName.ContinuePlay]: PlayerEventHandler<never>;
    [TrackerTrackEventName.ClickPlay]: PlayerEventHandler<never>;
    [TrackerTrackEventName.PauseResume]: PlayerEventHandler<never>;
  };
  windowEvents: {
    beforeunload: PlayerEventHandler<BeforeUnloadEvent>;
  };
}
export type CombinedEventHandlers = typeof eventHandlers[keyof (typeof eventHandlers)];
